import type { ImageAnalyses } from '@/@types/latestImagesV2';
import type { GetClaimSummaryResponse } from '@/modules/claims/freeClaims/insuranceExport/types';

import { getImageTeethIdsForSelectedTreatments } from '../../../helpers';
import type {
  FilterState,
  RadiographSubtype,
} from '../../../hooks/useImageSelectionModalState';

/** ISO date pattern (YYYY-MM-DD) at start of string; avoids timezone shift for date-only values. */
const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

export const getVisitKeyFromDate = (date: string): string | null => {
  const dateOnlyMatch = date.match(ISO_DATE_PREFIX);
  if (dateOnlyMatch) {
    return `${dateOnlyMatch[1]}-${dateOnlyMatch[2]}-${dateOnlyMatch[3]}`;
  }
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const getVisitKeyFromImage = (img: ImageAnalyses): string | null => {
  if (!img.imageAcquiredAt) return null;
  return getVisitKeyFromDate(img.imageAcquiredAt);
};

export const parseImageFromID = (
  imageID: string,
  filteredRadiographs: ImageAnalyses[],
  filteredColorImages: ImageAnalyses[],
): ImageAnalyses | undefined => {
  const parts = imageID.split('-');
  if (parts.length < 2) return undefined;
  const index = parseInt(parts[1], 10);
  if (Number.isNaN(index) || index < 0) return undefined;
  if (parts[0] === 'color') {
    return index < filteredColorImages.length
      ? filteredColorImages[index]
      : undefined;
  }
  return index < filteredRadiographs.length
    ? filteredRadiographs[index]
    : undefined;
};

export const parseImageTeethIdsFromID = (
  imageID: string,
  claimSummary: GetClaimSummaryResponse | undefined,
  filteredRadiographs: ImageAnalyses[],
  filteredColorImages: ImageAnalyses[],
  selectedTreatmentIds: string[],
): string[] => {
  const image = parseImageFromID(
    imageID,
    filteredRadiographs,
    filteredColorImages,
  );
  if (!image) return [];
  const { imagesDisplay: claimSummaryImagesDisplay } = claimSummary ?? {};
  const matchingDisplayItem = claimSummaryImagesDisplay?.find(
    (item) => item.id === image.imageId,
  );
  const imageTeethIds = getImageTeethIdsForSelectedTreatments(
    matchingDisplayItem,
    selectedTreatmentIds,
  );
  return imageTeethIds;
};

export const buildInitialFilterState = (params: {
  filteredRadiographs: ImageAnalyses[];
  filteredColorImages: ImageAnalyses[];
  selectedImageKeys: string[] | null;
  hasQuadLevelSummary: boolean;
  fmxGrid: (ImageAnalyses | null)[];
  claimSummary: GetClaimSummaryResponse | undefined;
  showFMX: boolean;
  showPerioChart: boolean;
}): FilterState => {
  const {
    filteredRadiographs,
    filteredColorImages,
    selectedImageKeys,
    hasQuadLevelSummary,
    fmxGrid,
    claimSummary,
    showFMX,
    showPerioChart,
  } = params;
  // Only apply "best intraoral only" when a *visible* (filtered) display item has intraoralOrder 0
  const filteredColorImageIds = new Set(
    filteredColorImages.map((img) => img.imageId),
  );
  const displayItemsForVisibleImages =
    claimSummary?.imagesDisplay?.filter((item) =>
      filteredColorImageIds.has(item.id),
    ) ?? [];
  const defaultBestIntraoralOnly = displayItemsForVisibleImages.some(
    (item) =>
      item.intraoralOrder !== undefined && item.intraoralOrder === 0,
  );

  const selectedSet =
    selectedImageKeys === null
      ? new Set([
          ...filteredRadiographs.map((_, i) => `radiograph-${i}`),
          ...(defaultBestIntraoralOnly ? ['color-0'] : filteredColorImages.map((_, i) => `color-${i}`)),
        ])
      : new Set(selectedImageKeys);
  const state: FilterState = {};

  const ensureDate = (visitKey: string) => {
    if (!state[visitKey]) {
      state[visitKey] = {
        radiographs: {},
        colorImages: {},
        'fmx-selected': false,
        'fmx-present': false,
        'periochart-present': false,
        'periochart-selected': false,
      };
    }
    return state[visitKey];
  };

  filteredRadiographs.forEach((img, index) => {
    const visitKey = getVisitKeyFromImage(img) ?? '_noDate';
    const dateState = ensureDate(visitKey);
    const typeKey = img.imageType as RadiographSubtype;
    const radByType = dateState.radiographs as Record<
      string,
      Record<string, boolean>
    >;
    if (!radByType[typeKey]) radByType[typeKey] = {};
    const key = `radiograph-${index}`;
    radByType[typeKey][key] = selectedSet.has(key);
  });

  filteredColorImages.forEach((img, index) => {
    const visitKey = getVisitKeyFromImage(img) ?? '_noDate';
    const dateState = ensureDate(visitKey);
    const key = `color-${index}`;
    dateState.colorImages[key] = selectedSet.has(key);
  });

  if (hasQuadLevelSummary) {
    const nonNullFmxGrid = fmxGrid.filter((img) => img != null);
    if (nonNullFmxGrid.length > 0) {
      const visitKey = getVisitKeyFromImage(nonNullFmxGrid[0]) ?? '_noDate';
      const dateState = ensureDate(visitKey);
      dateState['fmx-selected'] = showFMX ?? false;
      dateState['fmx-present'] = true;
    }
  }

  const perioChartDate: string | null | undefined =
    claimSummary?.perioChart?.examDate;
  if (perioChartDate) {
    const visitKey = getVisitKeyFromDate(perioChartDate) ?? '_noDate';
    const dateState = ensureDate(visitKey);
    dateState['periochart-present'] = true;
    dateState['periochart-selected'] = showPerioChart ?? false;
  }
  return state;
};

export const RADIOGRAPH_SECTIONS: {
  sectionTitle: string;
  radiographTypeKey: 'BITEWING' | 'PERIAPICAL' | 'PANORAMIC';
}[] = [
  { sectionTitle: 'Bitewings', radiographTypeKey: 'BITEWING' },
  { sectionTitle: 'Panoramic', radiographTypeKey: 'PANORAMIC' },
  { sectionTitle: 'Periapicals', radiographTypeKey: 'PERIAPICAL' },
];

export const IMAGE_TYPE_MENU_ITEMS = [
  { subtype: 'BITEWING' as const, label: 'Bitewings' },
  { subtype: 'PERIAPICAL' as const, label: 'Periapicals' },
  { type: 'fmx' as const, label: 'FMX' },
  { subtype: 'PANORAMIC' as const, label: 'Panoramic' },
] as const;
